import { useEffect, useRef, useState, useCallback } from "react";
import { Images, Loader2, UploadCloud, Trash2, Check, Rocket, Save, Sparkles } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { imgUrl } from "@/lib/format";
import { compressImage } from "@/lib/imageCompress";
import { Input } from "@/components/ui/input";
import { SubcategoryInput } from "@/components/SubcategoryInput";
import { TrayInput, loadTray, saveTray } from "@/components/TrayInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const KARATS = ["8", "14", "18", "22", "24"];
const inputCls = "bg-obsidian border-border text-foreground focus-visible:ring-gold h-9";

export default function BulkPhotos() {
  const [categories, setCategories] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [edits, setEdits] = useState({}); // id -> {karat,gram,milyem,name,category}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [uploadingExtra, setUploadingExtra] = useState(false);
  const [progressExtra, setProgressExtra] = useState({ done: 0, total: 0 });
  const [dragOverExtra, setDragOverExtra] = useState(false);
  const extraFileRef = useRef();
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [bulkKarat, setBulkKarat] = useState("");
  const [bulkMilyem, setBulkMilyem] = useState("");
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkSubcategory, setBulkSubcategory] = useState("");
  const [bulkTray, setBulkTray] = useState(loadTray());
  const [bulkGroup, setBulkGroup] = useState("");
  const [bulkName, setBulkName] = useState("");
  const [bulkGramMin, setBulkGramMin] = useState("");
  const [bulkGramMax, setBulkGramMax] = useState("");
  const [bulkSizes, setBulkSizes] = useState("");
  const fileRef = useRef();

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products/drafts");
      setDrafts(data);
      const e = {};
      data.forEach((d) => {
        e[d.id] = {
          karat: d.karat || "22", gram: d.gram || "", milyem: d.milyem || "", gram_min: d.gram_min ?? "", gram_max: d.gram_max ?? "", sizes: (d.sizes || []).join(", "),
          name: d.name === d.code ? "" : (d.name || ""), category: d.category || "", subcategory: d.subcategory || "", tray: d.tray || "", group: d.group || "",
        };
      });
      setEdits(e);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Taslaklar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    loadDrafts();
  }, [loadDrafts]);

  // Upload files with limited concurrency; compress first; preserves order (null = failed).
  const uploadBatch = async (files, onProgress) => {
    const results = new Array(files.length);
    let done = 0, idx = 0;
    const LIMIT = 3;
    const worker = async () => {
      while (idx < files.length) {
        const i = idx++;
        try {
          const cf = await compressImage(files[i]);
          const fd = new FormData();
          fd.append("file", cf);
          const up = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
          results[i] = up.data.url;
        } catch {
          results[i] = null;
        }
        done++;
        onProgress(done);
      }
    };
    await Promise.all(Array.from({ length: Math.min(LIMIT, files.length) }, worker));
    return results;
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || [])
      .filter((f) => f.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    if (files.length === 0) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const results = await uploadBatch(files, (d) => setProgress({ done: d, total: files.length }));
    const urls = results.filter(Boolean);
    const fail = results.length - urls.length;
    try {
      if (urls.length) await api.post("/admin/products/drafts", { image_urls: urls, tray: bulkTray });
      toast.success(`${urls.length} taslak eklendi${fail ? `, ${fail} yüklenemedi` : ""}`);
      await loadDrafts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Taslak oluşturulamadı");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!uploading) handleFiles(e.dataTransfer.files);
  };

  const handleExtraFiles = async (fileList) => {
    const files = Array.from(fileList || [])
      .filter((f) => f.type.startsWith("image/"))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    if (files.length === 0) return;
    if (drafts.length === 0) {
      toast.error("Önce kapak görsellerini yükleyin");
      return;
    }
    setUploadingExtra(true);
    setProgressExtra({ done: 0, total: files.length });
    const results = await uploadBatch(files, (d) => setProgressExtra({ done: d, total: files.length }));
    const urls = results.map((r) => r || "");
    const fail = results.filter((r) => !r).length;
    try {
      const { data } = await api.post("/admin/products/drafts/append-images", { image_urls: urls });
      toast.success(`${data.updated} taslağa ek görsel eklendi${data.extra ? `, ${data.extra} fazla görsel atlandı` : ""}${fail ? `, ${fail} yüklenemedi` : ""}`);
      await loadDrafts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Ek görseller eklenemedi");
    } finally {
      setUploadingExtra(false);
      if (extraFileRef.current) extraFileRef.current.value = "";
    }
  };

  const onDropExtra = (e) => {
    e.preventDefault();
    setDragOverExtra(false);
    if (!uploadingExtra) handleExtraFiles(e.dataTransfer.files);
  };

  const setField = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  // ---- Hızlı gram girişi (klavye akışı) ----
  const [savedGram, setSavedGram] = useState({});
  const gminRefs = useRef({});
  const focusGmin = (i) => { const el = gminRefs.current[i]; if (el) { el.focus(); el.select?.(); } };
  const saveGramBg = async (d) => {
    const e = edits[d.id] || {};
    const gmin = e.gram_min ? parseFloat(String(e.gram_min).replace(",", ".")) : null;
    const gmax = e.gram_max ? parseFloat(String(e.gram_max).replace(",", ".")) : null;
    if (gmin == null || gmax == null || isNaN(gmin) || isNaN(gmax)) return;
    setSavedGram((s) => ({ ...s, [d.id]: true }));
    try {
      await api.post("/admin/products/drafts/save", { items: [{
        id: d.id, karat: e.karat || "22",
        milyem: e.milyem === "" ? null : parseFloat(e.milyem),
        gram_min: gmin, gram_max: gmax,
        sizes: e.sizes ? e.sizes.split(",").map((s) => s.trim()).filter(Boolean) : null,
        name: e.name || null, category: e.category || "", subcategory: e.subcategory || "",
        tray: e.tray || "", group: e.group || "",
      }], publish: false });
    } catch { setSavedGram((s) => ({ ...s, [d.id]: false })); }
  };
  const handleGramKey = (ev, di, d) => {
    if (ev.key === "Enter") {
      ev.preventDefault();
      const e = edits[d.id] || {};
      const okRange = String(e.gram_min ?? "").trim() !== "" && String(e.gram_max ?? "").trim() !== "";
      if (!ev.shiftKey && !okRange) { toast.error("Tahmini aralık girilmedi. (Atlamak için Shift+Enter)"); return; }
      if (okRange) saveGramBg(d);
      focusGmin(di + 1);
    } else if (ev.key === "ArrowUp") { ev.preventDefault(); focusGmin(di - 1); }
    else if (ev.key === "ArrowDown") { ev.preventDefault(); focusGmin(di + 1); }
    else if (ev.key === "Escape") { ev.currentTarget.blur(); }
  };
  useEffect(() => {
    if (drafts.length > 0) {
      const idx = drafts.findIndex((d) => !((edits[d.id] || {}).gram_min));
      if (idx >= 0) setTimeout(() => focusGmin(idx), 60);
    }
    // eslint-disable-next-line
  }, [drafts.length]);

  const buildItems = () => drafts.map((d) => {
    const e = edits[d.id] || {};
    return {
      id: d.id,
      karat: e.karat || "22",
      milyem: e.milyem === "" ? null : parseFloat(e.milyem),
      gram_min: e.gram_min ? parseFloat(e.gram_min) : null,
      gram_max: e.gram_max ? parseFloat(e.gram_max) : null,
      sizes: e.sizes ? e.sizes.split(",").map((s) => s.trim()).filter(Boolean) : null,
      name: e.name || null,
      category: e.category || "",
      subcategory: e.subcategory || "",
      tray: e.tray || "",
      group: e.group || "",
    };
  });

  const applyToAll = () => {
    if (drafts.length === 0) return;
    saveTray(bulkTray);
    setEdits((prev) => {
      const next = { ...prev };
      drafts.forEach((d) => {
        next[d.id] = {
          ...next[d.id],
          ...(bulkKarat ? { karat: bulkKarat } : {}),
          ...(bulkMilyem !== "" ? { milyem: bulkMilyem } : {}),
          ...(bulkCategory ? { category: bulkCategory } : {}),
          ...(bulkSubcategory ? { subcategory: bulkSubcategory } : {}),
          ...(bulkTray ? { tray: bulkTray } : {}),
          ...(bulkGroup ? { group: bulkGroup } : {}),
          ...(bulkName ? { name: bulkName } : {}),
          ...(bulkGramMin !== "" ? { gram_min: bulkGramMin } : {}),
          ...(bulkGramMax !== "" ? { gram_max: bulkGramMax } : {}),
          ...(bulkSizes ? { sizes: bulkSizes } : {}),
        };
      });
      return next;
    });
    toast.success("Tüm taslaklara uygulandı");
  };

  const save = async (publish) => {
    if (drafts.length === 0) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/products/drafts/save", { items: buildItems(), publish });
      if (publish) {
        if (data.published > 0) toast.success(`${data.published} ürün yayınlandı`);
        if (data.skipped?.length) toast.warning(`${data.skipped.length} ürün eksik bilgi (Gram/Milyem) nedeniyle yayınlanmadı`);
        if (data.published === 0 && !data.skipped?.length) toast.info("Yayınlanacak ürün yok");
      } else {
        toast.success("Taslaklar kaydedildi");
      }
      await loadDrafts();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      setDrafts((d) => d.filter((x) => x.id !== id));
      toast.success("Taslak silindi");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Silinemedi");
    }
  };

  const readyCount = drafts.filter((d) => {
    const e = edits[d.id] || {};
    return e.gram_min !== "" && e.gram_max !== "" && e.milyem !== "" && e.karat;
  }).length;

  return (
    <div className="max-w-6xl pb-28" data-testid="bulk-photos-page">
      <div className="flex items-center gap-2 mb-1">
        <Images className="text-gold" size={22} />
        <h1 className="font-display text-3xl text-foreground">Toplu Fotoğraf</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Gemlightbox fotoğraflarını sürükleyip bırakın. Her fotoğraf otomatik <b>SKU</b> ve <b>22 ayar</b> ile taslak olur; aşağıda tahmini gram aralığı ve Milyem girip toplu yayınlayın.
      </p>

      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="bulk-file-input" onChange={(e) => handleFiles(e.target.files)} />
      <div
        data-testid="bulk-dropzone"
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 h-36 ${dragOver ? "border-gold bg-gold/10" : "border-border bg-surface hover:border-gold/60"}`}
      >
        {uploading ? (
          <>
            <Loader2 className="animate-spin text-gold" size={26} />
            <span className="text-sm text-muted-foreground">Yükleniyor {progress.done}/{progress.total}</span>
          </>
        ) : (
          <>
            <UploadCloud className="text-gold" size={28} />
            <span className="text-sm text-foreground">Fotoğrafları sürükleyin veya tıklayıp seçin (çoklu)</span>
          </>
        )}
      </div>

      {drafts.length > 0 && (
        <>
          <input ref={extraFileRef} type="file" accept="image/*" multiple className="hidden" data-testid="bulk-extra-file-input" onChange={(e) => handleExtraFiles(e.target.files)} />
          <div className="mt-4">
            <div className="text-[10px] tracking-luxe uppercase text-gold mb-1">Ek Görseller (sıraya göre)</div>
            <div
              data-testid="bulk-extra-dropzone"
              onClick={() => !uploadingExtra && extraFileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOverExtra(true); }}
              onDragLeave={() => setDragOverExtra(false)}
              onDrop={onDropExtra}
              className={`cursor-pointer rounded-lg border-2 border-dashed transition-colors flex flex-col items-center justify-center gap-2 h-28 ${dragOverExtra ? "border-gold bg-gold/10" : "border-border bg-surface hover:border-gold/60"}`}
            >
              {uploadingExtra ? (
                <>
                  <Loader2 className="animate-spin text-gold" size={22} />
                  <span className="text-sm text-muted-foreground">Yükleniyor {progressExtra.done}/{progressExtra.total}</span>
                </>
              ) : (
                <>
                  <Images className="text-gold" size={24} />
                  <span className="text-sm text-foreground">2. görsel grubunu <b>aynı sırada</b> yükleyin (1. → 1. taslak, 2. → 2. taslak …)</span>
                  <span className="text-[11px] text-muted-foreground">Her görsel, sıradaki taslağın galerisine eklenir</span>
                </>
              )}
            </div>
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-6 mb-3">
        <div className="text-[10px] tracking-luxe uppercase text-gold">
          {loading ? "Yükleniyor…" : `${drafts.length} taslak · ${readyCount} hazır`}
        </div>
      </div>

      {drafts.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 bg-surface border border-border rounded-md p-3 mb-4" data-testid="bulk-apply-bar">
          <span className="text-xs text-muted-foreground w-full sm:w-auto sm:mr-2 mb-1 sm:mb-2">Toplu uygula:</span>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Ayar</label>
            <Select value={bulkKarat || "none"} onValueChange={(v) => setBulkKarat(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="bulk-apply-karat" className={`${inputCls} w-24`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground">
                <SelectItem value="none">—</SelectItem>
                {KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Milyem</label>
            <Input data-testid="bulk-apply-milyem" type="number" inputMode="numeric" value={bulkMilyem} onChange={(ev) => setBulkMilyem(ev.target.value)} className={`${inputCls} w-24`} />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Tahmini Aralık (min–max gr)</label>
            <div className="flex items-center gap-1">
              <Input data-testid="bulk-apply-gmin" type="text" inputMode="decimal" value={bulkGramMin} onChange={(ev) => setBulkGramMin(ev.target.value.replace(",", "."))} placeholder="2.5" className={`${inputCls} w-20`} />
              <span className="text-muted-foreground">–</span>
              <Input data-testid="bulk-apply-gmax" type="text" inputMode="decimal" value={bulkGramMax} onChange={(ev) => setBulkGramMax(ev.target.value.replace(",", "."))} placeholder="3.5" className={`${inputCls} w-20`} />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Boylar (virgülle)</label>
            <Input data-testid="bulk-apply-sizes" value={bulkSizes} onChange={(ev) => setBulkSizes(ev.target.value)} placeholder="44, 45, 46" className={`${inputCls} w-32`} />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Kategori</label>
            <Select value={bulkCategory || "none"} onValueChange={(v) => { setBulkCategory(v === "none" ? "" : v); setBulkSubcategory(""); }}>
              <SelectTrigger data-testid="bulk-apply-category" className={`${inputCls} w-36`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground max-h-64">
                <SelectItem value="none">—</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Alt Kategori</label>
            <div className="w-40">
              <SubcategoryInput categories={categories} category={bulkCategory} value={bulkSubcategory} onChange={setBulkSubcategory} className={inputCls} testid="bulk-apply-subcategory" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-0.5">Grup</label>
            <Select value={bulkGroup || "none"} onValueChange={(v) => setBulkGroup(v === "none" ? "" : v)}>
              <SelectTrigger data-testid="bulk-apply-group" className={`${inputCls} w-32`}><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="bg-surface border-border text-foreground">
                <SelectItem value="none">Genel</SelectItem>
                <SelectItem value="6007">6007</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button data-testid="bulk-apply-button" onClick={applyToAll} className="h-9 px-4 bg-gold/90 text-obsidian font-medium rounded-md hover:bg-gold transition-colors text-sm">
            Tümüne Uygula
          </button>
          <div className="w-full sm:w-40">
            <label className="text-[10px] text-muted-foreground block mb-0.5">Ad (ops.)</label>
            <Input data-testid="bulk-apply-name" value={bulkName} onChange={(ev) => setBulkName(ev.target.value)} placeholder="Tüm taslaklar için ad" className={inputCls} />
          </div>
          <div className="w-full sm:w-48">
            <label className="text-[10px] text-muted-foreground block mb-0.5">Tabla</label>
            <TrayInput value={bulkTray} onChange={setBulkTray} className={inputCls} testid="bulk-apply-tray" />
          </div>
        </div>
      )}

      {!loading && drafts.length === 0 && (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="bulk-empty">
          Henüz taslak yok. Yukarıdan fotoğraf ekleyin.
        </div>
      )}

      <div className="space-y-3">
        {drafts.map((d, di) => {
          const e = edits[d.id] || {};
          const ready = e.gram_min !== "" && e.gram_max !== "" && e.milyem !== "" && e.karat;
          return (
            <div key={d.id} data-testid={`draft-row-${d.code}`} className="flex flex-col md:flex-row md:items-center gap-3 bg-surface border border-border rounded-md p-3">
              <div className="flex items-center gap-3 md:w-56 shrink-0">
                <div className="w-16 h-16 rounded overflow-hidden bg-elevated border border-border shrink-0 relative">
                  {d.image_url && <img src={imgUrl(d.image_url)} alt="" className="w-full h-full object-cover" />}
                  {(d.images?.length || 1) > 1 && (
                    <span data-testid={`draft-imgcount-${d.code}`} className="absolute bottom-0.5 right-0.5 bg-black/80 text-gold text-[10px] font-medium px-1 rounded flex items-center gap-0.5">
                      <Images size={10} /> {d.images.length}
                    </span>
                  )}
                </div>
                <div>
                  <div className="font-mono text-xs text-gold">{d.code}</div>
                  {ready ? (
                    <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Check size={12} /> Hazır</span>
                  ) : (
                    <span className="text-[10px] text-muted-foreground">Eksik bilgi</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-8 gap-2 flex-1">
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Ayar *</label>
                  <Select value={e.karat || "22"} onValueChange={(v) => setField(d.id, "karat", v)}>
                    <SelectTrigger data-testid={`draft-karat-${d.code}`} className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-border text-foreground">
                      {KARATS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Tahmini Aralık (min–max gr) *</label>
                  <div className="flex items-center gap-1">
                    <Input data-testid={`draft-gmin-${d.code}`} type="text" inputMode="decimal" value={e.gram_min || ""}
                      ref={(el) => { gminRefs.current[di] = el; }}
                      onChange={(ev) => { setField(d.id, "gram_min", ev.target.value.replace(",", ".")); setSavedGram((s) => ({ ...s, [d.id]: false })); }}
                      onKeyDown={(ev) => handleGramKey(ev, di, d)}
                      placeholder="2.5" className={`${inputCls} ${savedGram[d.id] ? "border-emerald-500/50" : ""}`} />
                    <span className="text-muted-foreground">–</span>
                    <Input data-testid={`draft-gmax-${d.code}`} type="text" inputMode="decimal" value={e.gram_max || ""}
                      onChange={(ev) => { setField(d.id, "gram_max", ev.target.value.replace(",", ".")); setSavedGram((s) => ({ ...s, [d.id]: false })); }}
                      onKeyDown={(ev) => handleGramKey(ev, di, d)}
                      placeholder="3.5" className={inputCls} />
                    {savedGram[d.id] && <span data-testid={`draft-gram-saved-${d.code}`} className="text-emerald-400 text-xs">✓</span>}
                  </div>
                </div>
                {e.category === "Alyans" && (
                  <div>
                    <label className="text-[10px] text-muted-foreground block mb-0.5">Boylar (virgülle)</label>
                    <Input data-testid={`draft-sizes-${d.code}`} value={e.sizes || ""} onChange={(ev) => setField(d.id, "sizes", ev.target.value)} placeholder="44, 45, 46" className={inputCls} />
                  </div>
                )}
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Milyem *</label>
                  <Input data-testid={`draft-milyem-${d.code}`} type="number" inputMode="numeric" value={e.milyem} onChange={(ev) => setField(d.id, "milyem", ev.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Kategori</label>
                  <Select value={e.category || "none"} onValueChange={(v) => setField(d.id, "category", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={`draft-category-${d.code}`} className={inputCls}><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent className="bg-surface border-border text-foreground max-h-64">
                      <SelectItem value="none">—</SelectItem>
                      {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Alt Kategori</label>
                  <SubcategoryInput categories={categories} category={e.category} value={e.subcategory} onChange={(v) => setField(d.id, "subcategory", v)} className={inputCls} testid={`draft-subcategory-${d.code}`} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Ad (ops.)</label>
                  <Input data-testid={`draft-name-${d.code}`} value={e.name} onChange={(ev) => setField(d.id, "name", ev.target.value)} placeholder="Otomatik" className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Tabla</label>
                  <Input data-testid={`draft-tray-${d.code}`} value={e.tray || ""} onChange={(ev) => setField(d.id, "tray", ev.target.value)} placeholder="Örn. TABLA-01" className={inputCls} />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-0.5">Grup</label>
                  <Select value={e.group || "none"} onValueChange={(v) => setField(d.id, "group", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid={`draft-group-${d.code}`} className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-border text-foreground">
                      <SelectItem value="none">Genel</SelectItem>
                      <SelectItem value="6007">6007</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <button data-testid={`draft-delete-${d.code}`} onClick={() => deleteDraft(d.id)} className="shrink-0 self-end md:self-center p-2 text-muted-foreground hover:text-red-400 transition-colors">
                <Trash2 size={18} />
              </button>
            </div>
          );
        })}
      </div>

      {drafts.length > 0 && (
        <div className="fixed bottom-0 left-0 md:left-64 right-0 z-30 backdrop-blur-xl bg-black/85 border-t border-border px-5 md:px-8 py-3 flex items-center justify-end gap-3">
          <button data-testid="bulk-save-draft" onClick={() => save(false)} disabled={saving} className="h-11 px-5 border border-border rounded-md text-sm text-foreground hover:border-gold transition-colors flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Taslak Kaydet
          </button>
          <button data-testid="bulk-publish" onClick={() => save(true)} disabled={saving} className="h-11 px-6 bg-gold text-obsidian font-semibold rounded-md hover:bg-gold-hover transition-colors flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Rocket size={16} />} Tümünü Yayınla ({readyCount})
          </button>
        </div>
      )}
    </div>
  );
}
